/**
 * SanctaLauncher — floating rail-tab / pill launcher for the Sancta widget.
 *
 * Now wraps the button in a LAYERED ANIMATED GLOW RING driven by `activity`:
 *   · processing — fast pulsing ring (working on it)
 *   · listening  — slow steady breathing ring (mic open)
 *   · results    — brief one-shot burst (answer/products delivered)
 *   · idle       — calm, static, no rings
 *   · sleeping   — dimmed, animations off (wakes on interaction)
 */
import React from "react";
import { MessageCircle, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

type DragHandle = {
  pos: { dragged: boolean; x: number; y: number };
  wasClick: boolean;
  draggableProps: any;
};

export type SanctaActivity = "idle" | "listening" | "processing" | "results" | "sleeping";

interface Props {
  open: boolean;
  drag: DragHandle;
  onToggle: () => void;
  chatbotName: string;
  activity?: SanctaActivity;
}

const Ring: React.FC<{ color: string; anim: any }> = ({ color, anim }) => (
  <motion.span
    aria-hidden
    className="absolute inset-0 rounded-full pointer-events-none"
    style={{ boxShadow: `0 0 0 2px ${color}` }}
    initial={{ opacity: 0, scale: 1 }}
    {...anim}
  />
);

export const SanctaLauncher: React.FC<Props> = ({ open, drag, onToggle, chatbotName, activity = "idle" }) => {
  const positionStyle = drag.pos.dragged
    ? { position: "fixed" as const, left: drag.pos.x, top: drag.pos.y, right: "auto" as const, bottom: "auto" as const, transform: "none" }
    : { position: "fixed" as const, right: 0, top: "50%", transform: "translateY(-50%)" };

  const sleeping = activity === "sleeping";

  return (
    <div style={positionStyle} className="z-[95] sancta-launcher" data-testid="sancta-launcher-wrap">
      <div className="relative h-14 w-14 grid place-items-center">
        {/* Animated glow rings behind the button */}
        <AnimatePresence>
          {activity === "processing" && (
            <>
              <Ring key="p1" color="rgba(249,115,22,0.7)" anim={{ animate: { opacity: [0.7, 0], scale: [1, 1.7] }, transition: { duration: 0.9, repeat: Infinity, ease: "easeOut" } }} />
              <Ring key="p2" color="rgba(217,70,239,0.6)" anim={{ animate: { opacity: [0.6, 0], scale: [1, 1.9] }, transition: { duration: 0.9, repeat: Infinity, ease: "easeOut", delay: 0.45 } }} />
            </>
          )}
          {activity === "listening" && (
            <Ring key="l1" color="rgba(244,63,94,0.6)" anim={{ animate: { opacity: [0.15, 0.6, 0.15], scale: [1, 1.35, 1] }, transition: { duration: 2.2, repeat: Infinity, ease: "easeInOut" } }} />
          )}
          {activity === "results" && (
            <Ring key="r1" color="rgba(16,185,129,0.85)" anim={{ animate: { opacity: [0.85, 0], scale: [1, 2.1] }, transition: { duration: 0.7, ease: "easeOut" } }} />
          )}
        </AnimatePresence>

        <button
          data-testid="sancta-launcher"
          data-activity={activity}
          data-dragged={drag.pos.dragged || undefined}
          onClick={() => { if (!drag.wasClick) return; onToggle(); }}
          aria-label={open ? `Close ${chatbotName}` : `Open ${chatbotName} assistant`}
          {...drag.draggableProps}
          style={{ ...drag.draggableProps.style, position: "relative" }}
          className={`relative h-14 shadow-lg shadow-black/25 grid place-items-center select-none cursor-grab active:cursor-grabbing transition-all duration-500
                      ${drag.pos.dragged ? "w-14 rounded-full" : "w-12 rounded-l-2xl rounded-r-none"}
                      ${sleeping ? "opacity-50 grayscale" : "opacity-100"}
                      ${open ? "bg-neutral-900 text-white" : "bg-gradient-to-br from-orange-500 to-fuchsia-600 text-white hover:scale-[1.03]"}`}
        >
          {open
            ? <X className="h-6 w-6 pointer-events-none" />
            : <MessageCircle className={`h-6 w-6 pointer-events-none ${activity === "processing" ? "animate-pulse" : ""}`} />}
        </button>
      </div>
    </div>
  );
};

export default SanctaLauncher;
